import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "@/api/axios";
import { useAuth } from "@/store/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Shield, Loader2 } from "lucide-react";

interface PasswordCredential {
  id: string;
  password: string;
  name?: string;
}

interface WindowWithCredentials extends Window {
  PasswordCredential: new (init: PasswordCredential) => PasswordCredential;
}

export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(() => {
    if (typeof window === "undefined") return false;
    return Boolean(new URLSearchParams(window.location.search).get("magic_token"));
  });
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const magicToken = params.get("magic_token");
    if (!magicToken) return;
    api.get(`/api/auth/magic-link/${magicToken}`)
      .then((res) => {
        if (res.data.success) {
          setUser({
            id: res.data.user.id ?? 0,
            username: res.data.user.username,
            is_admin: res.data.user.is_admin,
            email: res.data.user.email,
            email_verified: res.data.user.email_verified,
          });
          navigate(res.data.user.is_admin ? "/admin" : "/dashboard");
        } else {
          setError(res.data.error || "Недействительная или просроченная ссылка");
        }
      })
      .catch(() => {
        setError("Ошибка входа по ссылке");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [navigate, setUser]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const formData = new URLSearchParams();
      formData.append("username", username);
      formData.append("password", password);
      const res = await api.post("/api/auth/login", formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "X-Requested-With": "XMLHttpRequest",
        },
      });
      if (res.data.success) {
        setUser({
          id: 0,
          username: res.data.user.username,
          is_admin: res.data.user.is_admin,
          email: res.data.user.email,
          email_verified: res.data.user.email_verified,
        });
        // Try to save password using Credential Management API
        try {
          const w = window as unknown as WindowWithCredentials;
          if ("PasswordCredential" in window) {
            const cred = new w.PasswordCredential({
              id: username,
              password: password,
              name: username,
            });
            await navigator.credentials.store(cred as unknown as Credential);
          }
        } catch {
          // ignore credential API errors
        }
        navigate(res.data.user.is_admin ? "/admin" : "/dashboard");
      } else {
        setError(res.data.error || "Неверный логин или пароль");
      }
    } catch {
      setError("Неверный логин или пароль");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4">
      <Card className="w-full max-w-md border-slate-700 bg-slate-900/80 backdrop-blur">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Shield className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl text-white">Secure Access Gateway</CardTitle>
          <p className="text-sm text-slate-400">Войдите в систему</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
            {error && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <label htmlFor="login" className="text-sm font-medium text-slate-300">Логин</label>
              <Input
                id="login"
                name="username"
                type="text"
                autoComplete="username"
                inputMode="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                required
                className="border-slate-700 bg-slate-800 text-white placeholder:text-slate-500"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="password" className="text-sm font-medium text-slate-300">Пароль</label>
              <Input
                id="password"
                name="password"
                autoComplete="current-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••"
                required
                className="border-slate-700 bg-slate-800 text-white placeholder:text-slate-500"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Войти
            </Button>
            <div className="text-center text-sm text-slate-400">
              Нет аккаунта?{" "}
              <Link to="/register" className="text-primary hover:underline">
                Зарегистрироваться
              </Link>
            </div>
            <div className="flex justify-between text-sm">
              <Link to="/forgot-password" className="text-primary hover:underline">
                Забыли пароль?
              </Link>
              <Link to="/magic-link" className="text-primary hover:underline">
                Войти по ссылке
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
